import { cn } from '@/lib/utils';
import { Button } from '@startupquickstart/frontend';
import { Check, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { buildWordTiles, isRelatedWordsWin, type WordTile } from './minigame-logic';
import { parseRelatedWords } from './types';
import { DemoNotConfigured } from './DemoChrome';
import type { DemoChallenge } from './types';

type GameState = 'playing' | 'won' | 'lost';

/** Web replica of the mobile related-words minigame (preview only). */
export function RelatedWordsDemo({ challenge }: { challenge: DemoChallenge }) {
  const { theme, words, decoyWords } = parseRelatedWords(challenge.minigameConfig);

  const [tiles, setTiles] = useState<WordTile[]>(() => buildWordTiles(words, decoyWords));
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [state, setState] = useState<GameState>('playing');

  useEffect(() => {
    setTiles(buildWordTiles(words, decoyWords));
    setSelected(new Set());
    setState('playing');
    // words/decoyWords are fresh arrays each render; key on the id to reset per challenge.
  }, [challenge.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (words.length < 2) {
    return (
      <DemoNotConfigured>
        This related-words challenge isn&apos;t fully configured yet — needs at least two correct
        words.
      </DemoNotConfigured>
    );
  }

  const toggle = (id: number) => {
    if (state !== 'playing') return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = () => {
    if (state !== 'playing') return;
    setState(isRelatedWordsWin(tiles, selected) ? 'won' : 'lost');
  };

  const reset = () => {
    setTiles(buildWordTiles(words, decoyWords));
    setSelected(new Set());
    setState('playing');
  };

  const tileClass = (tile: WordTile): string => {
    const isSelected = selected.has(tile.id);
    if (state === 'lost') {
      if (tile.isCorrect && isSelected) return 'border-emerald-500 bg-emerald-50 text-foreground';
      if (tile.isCorrect && !isSelected) return 'border-red-500 bg-red-50 text-red-600';
      if (!tile.isCorrect && isSelected) return 'border-red-500 bg-red-100 text-foreground';
      return 'border-border bg-card text-muted-foreground';
    }
    return isSelected
      ? 'border-primary bg-primary/10 text-foreground'
      : 'border-border bg-card text-foreground hover:bg-muted';
  };

  return (
    <div className="space-y-4">
      <span className="font-mono text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
        Related words
      </span>

      {!!theme && (
        <div className="rounded-xl bg-primary/10 p-3">
          <p className="text-xs text-muted-foreground">Theme</p>
          <p className="text-base font-semibold text-foreground">{theme}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {tiles.map((tile) => {
          const isSelected = selected.has(tile.id);
          return (
            <button
              key={tile.id}
              type="button"
              onClick={() => toggle(tile.id)}
              disabled={state !== 'playing'}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border-[1.5px] px-3.5 py-2 text-sm font-medium',
                tileClass(tile),
              )}
            >
              {state === 'lost' && tile.isCorrect && !isSelected && (
                <X className="h-3.5 w-3.5 text-red-500" />
              )}
              {state === 'lost' && tile.isCorrect && isSelected && (
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              )}
              {tile.word}
            </button>
          );
        })}
      </div>

      {state === 'won' && (
        <p className="rounded-xl bg-emerald-50 p-3 text-center text-sm font-semibold text-emerald-700">
          All matches found!
        </p>
      )}
      {state === 'lost' && (
        <p className="rounded-xl bg-red-50 p-3 text-center text-sm text-red-700">
          Not quite. Green = right, red = missing or decoy. Try again.
        </p>
      )}

      {state === 'playing' && (
        <Button onClick={submit} disabled={selected.size === 0}>
          Submit
        </Button>
      )}
      {(state === 'lost' || state === 'won') && (
        <Button variant="outline" onClick={reset}>
          {state === 'won' ? 'Play again' : 'Try again'}
        </Button>
      )}
    </div>
  );
}
